import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faEdit,
  faBookmark,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useAnnotations } from "../contexts/AnnotationsContext";
import {
  AnnotationType,
  Annotation,
  getAnnotationLabel,
} from "../utils/annotator/annotationsHelper";
import { translucentHighlightColor } from "../utils/annotator/highlightColors";
import NoteForm from "./NoteForm";
import DeleteAnnotationConfirmForm from "./DeleteAnnotationConfirmForm";
import { focusModalCloseButton } from "../utils/DomUtils";
import "./BookmarksAndNotesTab.scss";

const FILTERS: {
  key: AnnotationType | undefined;
  label: string;
  emptyText: string;
}[] = [
  {
    key: undefined,
    label: "All",
    emptyText:
      "Nothing saved yet. Add bookmarks, notes, and highlights while reading to grow this list.",
  },
  {
    key: AnnotationType.Bookmark,
    label: "Bookmarks",
    emptyText:
      "No bookmarks yet. Select the bookmark icon while reading to add it to this list.",
  },
  {
    key: AnnotationType.Note,
    label: "Notes",
    emptyText:
      "No notes yet. Select any text while reading to add a note to this list.",
  },
  {
    key: AnnotationType.Highlight,
    label: "Highlights",
    emptyText:
      "No highlights yet. Select any text while reading to add it to this list.",
  },
];

enum ViewState {
  Form = "form",
  List = "list",
}

interface BookmarksAndNotesTabProps {
  /** The annotation to scroll into view, or null. */
  annotationToScrollTo?: Annotation | null;
  /** Click handler for an annotation link; reads the item's `data-cfi` to navigate. */
  onAnnotationLinkClicked: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Filter selected on first render. */
  initialFilter?: AnnotationType | undefined;
  /** Whether this tab is the one currently shown. Switching away resets any
   * open inline editor/confirmation back to the list. */
  isActive?: boolean;
}

/**
 * Tab listing the reader's saved annotations (bookmarks, notes, and highlights).
 *
 * Renders filter buttons to narrow the list by annotation type and, below them,
 * the matching annotations sorted by CFI. Bookmarks render as {@link BookmarkItem}
 * (delete only); notes and highlights render as {@link NoteItem} (edit + delete).
 */
function BookmarksAndNotesTab({
  annotationToScrollTo,
  onAnnotationLinkClicked,
  initialFilter,
  isActive = true,
}: BookmarksAndNotesTabProps) {
  const {
    getAnnotationsSortedByCfi,
    removeNote,
    removeBookmark,
    saveNoteEdit,
  } = useAnnotations();
  const [filter, setFilter] = useState<AnnotationType | undefined>(
    initialFilter,
  );

  const [annotationToDelete, setAnnotationToDelete] =
    useState<Annotation | null>(null);
  const [annotationToEdit, setAnnotationToEdit] = useState<Annotation | null>(
    null,
  );
  const [pendingView, setPendingView] = useState<ViewState | null>(null);
  const [focusButtonId, setFocusButtonId] = useState<string>("");

  const annotations = useMemo(
    () => getAnnotationsSortedByCfi(filter),
    [getAnnotationsSortedByCfi, filter],
  );

  function focusById(id: string) {
    const element = document.getElementById(id);
    if (element) {
      element.focus();
    } else {
      focusModalCloseButton(".navigationPanel");
    }
  }

  useEffect(() => {
    if (!isActive) {
      setAnnotationToDelete(null);
      setAnnotationToEdit(null);
      setPendingView(null);
      setFocusButtonId("");
    }
  }, [isActive]);

  /* 
    This useEffect makes sure that the screen reader experience for managing 
    annotations through the Book Navigation panel works well across our supported
    screen readers.
     1. JAWS re-reads the whole navigation dialog if focus ever re-enters it,
        so the focused element must never unmount — focus would fall to
        <body> and the restore would count as re-entering the dialog.
     2. Safari/VoiceOver will not follow focus into an element inserted in
        the same task, so focus onto new content must happen in a post-paint
        effect.
     3. NVDA announces any interim focus stop (e.g. parking on the modal
        close button), so we can't pass focus there even though it is available
        on both list and form views
    To support all 3 browsers, there is a single frame where both the list and form view 
    are mounted at the same time - using CSS to hide one of the views without removing
    it from the accessibility tree.
    Tread carefully if you make edits here.
  */
  useEffect(() => {
    if (!pendingView) return;
    if (pendingView === ViewState.Form) {
      setPendingView(null);
      setFocusButtonId("");
      return;
    } else {
      focusById(focusButtonId);
      setPendingView(null);
      setFocusButtonId("");
      setAnnotationToDelete(null);
      setAnnotationToEdit(null);
    }
  }, [pendingView, focusButtonId]);

  const isFormOpen = Boolean(annotationToDelete || annotationToEdit);
  const showList = !isFormOpen || pendingView !== null;
  const outgoingViewClass = "bookmarksAndNotesTab__outgoingView";
  /* 
    Apply the visually collapsed styling rules for a single frame, so 
    the outgoing component stays in the accessibility tree but isn't 
    displayed 
  */
  const formWrapperClass =
    pendingView === ViewState.List ? outgoingViewClass : undefined;
  const listWrapperClass =
    pendingView === ViewState.Form ? outgoingViewClass : undefined;

  function startDelete(annotation: Annotation) {
    setAnnotationToDelete(annotation);
    setPendingView(ViewState.Form);
  }

  function startEdit(annotation: Annotation) {
    setAnnotationToEdit(annotation);
    setPendingView(ViewState.Form);
  }

  function confirmDelete() {
    if (!annotationToDelete) return;
    focusModalCloseButton(".navigationPanel");
    if (annotationToDelete.type === AnnotationType.Bookmark) {
      removeBookmark(annotationToDelete.location);
    } else {
      removeNote(annotationToDelete.location);
    }
    setAnnotationToDelete(null);
    setPendingView(null);
  }

  function cancelDelete() {
    if (!annotationToDelete) return;
    setPendingView(ViewState.List);
    setFocusButtonId(deleteButtonId(annotationToDelete.location));
  }

  function saveEdit(note: string | null, color: string) {
    if (!annotationToEdit) return;
    saveNoteEdit(annotationToEdit.location, note, color);
    setPendingView(ViewState.List);
    setFocusButtonId(editButtonId(annotationToEdit.location));
  }

  function cancelEdit() {
    if (!annotationToEdit) return;
    setPendingView(ViewState.List);
    setFocusButtonId(editButtonId(annotationToEdit.location));
  }

  useEffect(() => {
    const annotation = [
      ...document.querySelectorAll<HTMLElement>(
        ".bookmarksAndNotesTab__annotations [data-cfi]",
      ),
    ].find((el) => el.dataset.cfi === annotationToScrollTo?.location);
    if (!annotation) return;
    const row = annotation.closest(".annotationItem") ?? annotation;
    row.scrollIntoView({ block: "start" });
    annotation.focus?.({ preventScroll: true });
  }, [annotationToScrollTo]);

  const noResultsText =
    FILTERS.find((f) => f.key === filter)?.emptyText ?? "Nothing to show.";

  return (
    <Container>
      <div className={formWrapperClass}>
        {annotationToDelete && (
          <DeleteConfirmView
            annotation={annotationToDelete}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
          />
        )}
        {annotationToEdit && (
          <NoteEditorView
            annotation={annotationToEdit}
            onSave={saveEdit}
            onCancel={cancelEdit}
          />
        )}
      </div>
      <div className={listWrapperClass}>
        {showList && (
          <>
            <div
              className="d-flex flex-wrap bookmarksAndNotesTab__filters"
              role="radiogroup"
              aria-label="Filter annotations"
            >
              {FILTERS.map((f) => (
                <Button
                  key={f.label}
                  size="sm"
                  className="rounded-pill mr-2 mb-2"
                  variant={filter === f.key ? "primary" : "outline-secondary"}
                  aria-pressed={filter === f.key}
                  onClick={() => {
                    setFilter(f.key);
                  }}
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {annotations.length > 0 ? (
              <ul
                className="bookmarksAndNotesTab__annotations"
                aria-label="Bookmarks and notes"
              >
                {annotations.map((annotation) => (
                  <li
                    key={`${annotation.type}-${annotation.location}`}
                    className="bookmarksAndNotesTab__annotations-item"
                  >
                    {annotation.type === AnnotationType.Bookmark ? (
                      <BookmarkItem
                        annotation={annotation}
                        onAnnotationLinkClicked={onAnnotationLinkClicked}
                        onDelete={() => startDelete(annotation)}
                      />
                    ) : (
                      <NoteItem
                        annotation={annotation}
                        isHighlight={
                          annotation.type === AnnotationType.Highlight
                        }
                        onAnnotationLinkClicked={onAnnotationLinkClicked}
                        onEdit={() => startEdit(annotation)}
                        onDelete={() => startDelete(annotation)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>{noResultsText}</p>
            )}
          </>
        )}
      </div>
    </Container>
  );
}

interface IconButtonProps {
  id?: string;
  icon: IconProp;
  ariaLabel: string;
  onClick: () => void;
}

/** A borderless icon button, shared by the bookmark/note/highlight rows. */
function IconButton({ id, icon, ariaLabel, onClick }: IconButtonProps) {
  return (
    <Button
      id={id}
      variant="link"
      className="p-1 text-secondary"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <FontAwesomeIcon icon={icon} aria-hidden="true" />
    </Button>
  );
}

const editButtonId = (location: string) => `annotation-edit-${location}`;
const deleteButtonId = (location: string) => `annotation-delete-${location}`;

function annotationActionButtonLabel(
  action: string,
  text: string | null | undefined,
) {
  const excerpt = text?.trim().split(/\s+/).slice(0, 7).join(" ");
  return excerpt ? `${action} - ${excerpt}` : action;
}

interface BookmarkItemProps {
  annotation: Annotation;
  onAnnotationLinkClicked: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onDelete: () => void;
}

/** A bookmark row: bookmark icon + navigable text, with a delete action only. */
function BookmarkItem({
  annotation,
  onAnnotationLinkClicked,
  onDelete,
}: BookmarkItemProps) {
  return (
    <div className="annotationItem border-bottom py-3 d-flex align-items-start">
      <div className="annotationItem__content flex-grow-1">
        <span className="annotationItem__type text-muted small">Bookmark</span>
        <span className="d-flex align-items-center text-truncate">
          <FontAwesomeIcon
            icon={faBookmark}
            className="annotationItem__bookmarkIcon mr-2"
            aria-hidden="true"
          />
          <Button
            variant="link"
            role="link"
            className="annotationItem__bookmarkLink text-truncate p-0"
            data-cfi={annotation.location}
            onClick={onAnnotationLinkClicked}
          >
            {annotation.text}
          </Button>
        </span>
      </div>
      <div className="annotationItem__actions">
        <IconButton
          id={deleteButtonId(annotation.location)}
          icon={faTrashAlt}
          ariaLabel={annotationActionButtonLabel(
            "Delete Bookmark",
            annotation.text,
          )}
          onClick={onDelete}
        />
      </div>
    </div>
  );
}

interface NoteItemProps {
  annotation: Annotation;
  isHighlight: boolean;
  onAnnotationLinkClicked: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * A note or highlight row: the highlighted text on a swatch in the annotation's
 * color, the note text below it (notes only), and edit + delete actions.
 */
function NoteItem({
  annotation,
  isHighlight,
  onAnnotationLinkClicked,
  onEdit,
  onDelete,
}: NoteItemProps) {
  const label = getAnnotationLabel(annotation.type);
  const actionText = !isHighlight ? annotation.note : annotation.text;

  return (
    <div className="annotationItem border-bottom py-3 d-flex align-items-start">
      <div className="annotationItem__content flex-grow-1">
        <span className="annotationItem__type text-muted small">{label}</span>
        <Button
          variant="link"
          role="link"
          className="annotationItem__highlight p-0"
          style={{
            backgroundColor: translucentHighlightColor(annotation.color),
          }}
          data-cfi={annotation.location}
          onClick={onAnnotationLinkClicked}
        >
          {annotation.text}
        </Button>
        {!isHighlight && (
          <span className="annotationItem__note d-block text-truncate mb-0 mt-1">
            {annotation.note}
          </span>
        )}
      </div>
      <div className="annotationItem__actions">
        <IconButton
          id={editButtonId(annotation.location)}
          icon={faEdit}
          ariaLabel={annotationActionButtonLabel(`Edit ${label}`, actionText)}
          onClick={onEdit}
        />
        <IconButton
          id={deleteButtonId(annotation.location)}
          icon={faTrashAlt}
          ariaLabel={annotationActionButtonLabel(`Delete ${label}`, actionText)}
          onClick={onDelete}
        />
      </div>
    </div>
  );
}

interface NoteEditorViewProps {
  annotation: Annotation;
  /** Save the edit. Receives the trimmed note (or `null` if blank) and color. */
  onSave: (note: string | null, color: string) => void;
  /** Leave the editor without saving. */
  onCancel: () => void;
}

/**
 * Inline note/highlight editor rendered in place of the list. Focus moves to the
 * note field on open and returns to the row's Edit button when it closes, so the
 * whole edit happens inside the panel's single dialog.
 */
function NoteEditorView({ annotation, onSave, onCancel }: NoteEditorViewProps) {
  const label = getAnnotationLabel(annotation.type);

  return (
    <>
      <h3 className="h5 mb-2">Edit {label}</h3>
      <NoteForm
        mode="edit"
        annotation={annotation}
        onEdit={onSave}
        onCancel={onCancel}
      />
    </>
  );
}

interface DeleteConfirmViewProps {
  annotation: Annotation;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Inline delete confirmation rendered in place of the list, wrapping the shared
 * {@link DeleteAnnotationConfirmForm}. Focus moves to the heading on open.
 */
function DeleteConfirmView({
  annotation,
  onConfirm,
  onCancel,
}: DeleteConfirmViewProps) {
  const label = getAnnotationLabel(annotation.type);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <>
      <h3 className="h5" tabIndex={-1} ref={headingRef}>
        Delete {label}
      </h3>
      <DeleteAnnotationConfirmForm
        annotation={annotation}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
}

export default BookmarksAndNotesTab;
